import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Snackbar,
  IconButton,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { NumericFormat, PatternFormat } from 'react-number-format';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      valueIsNumericString
      prefix="$"
    />
  );
});

const PhoneNumberFormat = React.forwardRef(function PhoneNumberFormat(props, ref) {
  const { onChange, ...other } = props;

  return (
    <PatternFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      format="+1 (###) ###-####"
      mask="_"
      valueIsNumericString
    />
  );
});

function Settings() {
  const [formData, setFormData] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    whatsapp_number: '',
    instagram: '',
    tiktok: '',
    youtube: '',
    twitter: '',
    linkedin: '',
    snapchat: '',
    primary_communication: 'email',
    minimum_deal_size: '',
    min_deal_terms: '',
    notify_new_deals: false,
    mark_emails_read: false,
    auto_respond: false,
    preferred_categories: '',
    email_permission: false,
    sms_permission: false,
    whatsapp_permission: false,
    gmail_token_valid: false,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/user`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // 'ngrok-skip-browser-warning': 'true',
          'Authorization': `Token ${localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const parsed = await response.json();
        let data = parsed.result;
        setFormData({
          username: data.username,
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          phone_number: data.phone_number,
          whatsapp_number: data.whatsapp_number,
          instagram: data.instagram,
          tiktok: data.tiktok,
          youtube: data.youtube,
          twitter: data.twitter,
          linkedin: data.linkedin,
          snapchat: data.snapchat,
          primary_communication: data.primary_communication,
          minimum_deal_size: data.minimum_deal_size,
          min_deal_terms: data.min_deal_terms,
          notify_new_deals: data.notify_new_deals,
          mark_emails_read: data.mark_emails_read,
          auto_respond: data.auto_respond,
          preferred_categories: data.preferred_categories,
          email_permission: data.email_permission,
          sms_permission: data.sms_permission,
          whatsapp_permission: data.whatsapp_permission,
          gmail_token_valid: data.gmail_token ? true : false,
        });
      } else {
        throw new Error('Failed to fetch user data');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setSnackbar({ open: true, message: 'Error fetching user data' });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          // 'ngrok-skip-browser-warning': 'true',
          'Authorization': `Token ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'Profile updated successfully!' });
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbar({ open: true, message: 'Error updating profile' });
    }
  };

  const handleGrantEmailAccess = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/user/gmail/auth`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // 'ngrok-skip-browser-warning': 'true',
          'Authorization': `Token ${localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.result.auth_url) {
          window.location.href = data.result.auth_url;
        } else {
          setSnackbar({ open: true, message: 'Failed to get authorization URL' });
        }
      } else {
        const errorData = await response.json();
        setSnackbar({ open: true, message: `Failed to initiate Gmail authorization: ${errorData.error}` });
      }
    } catch (error) {
      console.error('Error:', error);
      setSnackbar({ open: true, message: 'An error occurred while trying to grant email access' });
    }
  };

  const handleLogout = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/public/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // 'ngrok-skip-browser-warning': 'true',
          'Authorization': `Token ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        throw new Error('Logout failed');
      }
    } catch (error) {
      console.error('Error logging out:', error);
      setSnackbar({ open: true, message: 'Error logging out' });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <IconButton onClick={() => navigate('/dashboard')} color="primary">
          <ArrowBackIcon />{"Back"}
        </IconButton>
        <Button onClick={handleLogout} color="primary" variant="contained">
          Logout
        </Button>
      </Box>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <StyledPaper>
              <Typography variant="h5" gutterBottom>
                Profile
              </Typography>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                margin="normal"
              />
              <Box display="flex" alignItems="center">
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                />
                {formData.gmail_token_valid ? (
                  <CheckCircleIcon color="success" style={{ marginLeft: '10px' }} />
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleGrantEmailAccess}
                    style={{ marginLeft: '10px', px: 10 }}
                  >
                    verify
                  </Button>
                )}
              </Box>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                InputProps={{
                  inputComponent: PhoneNumberFormat,
                }}
                margin="normal"
              />
              <TextField
                fullWidth
                label="WhatsApp Number"
                name="whatsapp_number"
                value={formData.whatsapp_number}
                onChange={handleChange}
                InputProps={{
                  inputComponent: PhoneNumberFormat,
                }}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Instagram"
                name="instagram"
                value={formData.instagram}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="TikTok"
                name="tiktok"
                value={formData.tiktok}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="YouTube"
                name="youtube"
                value={formData.youtube}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Twitter"
                name="twitter"
                value={formData.twitter}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="LinkedIn"
                name="linkedin"
                value={formData.linkedin}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Snapchat"
                name="snapchat"
                value={formData.snapchat}
                onChange={handleChange}
                margin="normal"
              />
            </StyledPaper>
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledPaper>
              <Typography variant="h5" gutterBottom>
                Preferences
              </Typography>
              <FormControl fullWidth margin="normal">
                <InputLabel>Primary Communication Method</InputLabel>
                <Select
                  name="primary_communication"
                  value={formData.primary_communication}
                  onChange={handleChange}
                >
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Minimum Deal Size"
                name="minimum_deal_size"
                value={formData.minimum_deal_size}
                onChange={handleChange}
                InputProps={{
                  inputComponent: NumberFormatCustom,
                }}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Minimum Deal Terms"
                name="min_deal_terms"
                multiline
                rows={4}
                value={formData.min_deal_terms}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Preferred Categories"
                name="preferred_categories"
                multiline
                rows={4}
                value={formData.preferred_categories}
                onChange={handleChange}
                margin="normal"
              />
              <Box display="flex" justifyContent="space-between">
                <Box display="flex" flexDirection="column" mr={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.email_permission}
                        onChange={handleChange}
                        name="email_permission"
                        disabled={!formData.gmail_token_valid}
                      />
                    }
                    label="Auto Process Emails"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.auto_respond}
                        onChange={handleChange}
                        name="auto_respond"
                      />
                    }
                    label="Auto Send Responses"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.mark_emails_read}
                        onChange={handleChange}
                        name="mark_emails_read"
                      />
                    }
                    label="Mark Emails as Read"
                  />
                </Box>
                <Box display="flex" flexDirection="column">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.notify_new_deals}
                        onChange={handleChange}
                        name="notify_new_deals"
                      />
                    }
                    label="Notify Me of Deal Updates"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.sms_permission}
                        onChange={handleChange}
                        name="sms_permission"
                      />
                    }
                    label="Allow Text Messages"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.whatsapp_permission}
                        onChange={handleChange}
                        name="whatsapp_permission"
                      />
                    }
                    label="Allow WhatsApp Messages"
                  />
                </Box>
              </Box>
            </StyledPaper>
          </Grid>
        </Grid>
        <Button type="submit" variant="contained" color="primary" size="large" fullWidth mt={3}>
          Save Changes
        </Button>
      </form>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Container>
  );
}

export default Settings;
